import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import api, { BACKEND_URL } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Save, Send, Database, Search, Landmark, RotateCcw, AlertTriangle, ShoppingCart, Bell, Play, Download, FolderOpen, Upload, Image } from 'lucide-react';
import { formatDateTime } from '@/utils/dateFormat';

export default function AdminSettings() {
  const { admin: currentAdmin } = useAuth();
  const [mailSettings, setMailSettings] = useState({ client_id: '', client_secret: '', tenant_id: '', sender_email: 'info@gb-sylt.de', enabled: false });
  const [seo, setSeo] = useState({ meta_title: '', meta_description: '', keywords: '', slug: '', og_title: '', og_description: '', og_image: '' });
  const [bankSettings, setBankSettings] = useState({ kontoinhaber: '', bankname: '', iban: '', bic: '', hinweis: '' });
  const [shopSettings, setShopSettings] = useState({ min_order_value: 20, reminder_days: 2, service_fee: 5, default_category_id: '' });
  const [categories, setCategories] = useState([]);
  const [reminderStatus, setReminderStatus] = useState(null);
  const [backups, setBackups] = useState([]);
  const [testLoading, setTestLoading] = useState(false);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);

  // German umlaut normalization for sorting
  const normalizeGerman = (text) => {
    if (!text) return "";
    const replacements = [['ä', 'ae'], ['Ä', 'Ae'], ['ö', 'oe'], ['Ö', 'Oe'], ['ü', 'ue'], ['Ü', 'Ue'], ['ß', 'ss']];
    let result = text.toLowerCase();
    replacements.forEach(([old, newChar]) => { result = result.split(old).join(newChar); });
    return result;
  };

  useEffect(() => {
    api.get('/mail-settings').then(r => setMailSettings(r.data)).catch(() => {});
    api.get('/seo').then(r => setSeo(r.data)).catch(() => {});
    api.get('/bank-settings').then(r => setBankSettings(r.data)).catch(() => {});
    api.get('/shop-settings').then(r => setShopSettings(r.data)).catch(() => {});
    api.get('/backups').then(r => setBackups(r.data)).catch(() => {});
    api.get('/reminders/status').then(r => setReminderStatus(r.data)).catch(() => {});
    // Fetch categories for default category dropdown
    api.get('/categories').then(r => {
      // Sort alphabetically with German umlaut handling, only active categories
      const sorted = [...r.data]
        .filter(c => c.active !== false)
        .sort((a, b) => normalizeGerman(a.name || '').localeCompare(normalizeGerman(b.name || '')));
      setCategories(sorted);
    }).catch(() => {});
  }, []);

  const saveMailSettings = async () => {
    try {
      await api.put('/mail-settings', mailSettings);
      toast.success('Mail-Einstellungen gespeichert');
    } catch (e) { toast.error(`Fehler: ${e.response?.data?.detail || e.message}`); }
  };

  const testMail = async () => {
    setTestLoading(true);
    try {
      const res = await api.post('/mail-settings/test');
      toast.success(res.data.message);
    } catch (e) { toast.error(`Test fehlgeschlagen: ${e.response?.data?.detail || e.message}`); }
    finally { setTestLoading(false); }
  };

  const saveSeo = async () => {
    try {
      await api.put('/seo', seo);
      toast.success('SEO-Daten gespeichert');
    } catch (e) { toast.error(`Fehler: ${e.response?.data?.detail || e.message}`); }
  };

  const saveBankSettings = async () => {
    try {
      await api.put('/bank-settings', bankSettings);
      toast.success('Bankverbindung gespeichert');
    } catch (e) { toast.error(`Fehler: ${e.response?.data?.detail || e.message}`); }
  };

  const saveShopSettings = async () => {
    try {
      await api.put('/shop-settings', shopSettings);
      toast.success('Shop-Einstellungen gespeichert');
      // Refresh reminder status after saving
      api.get('/reminders/status').then(r => setReminderStatus(r.data)).catch(() => {});
    } catch (e) { toast.error(`Fehler: ${e.response?.data?.detail || e.message}`); }
  };

  const triggerReminderCheck = async () => {
    setReminderLoading(true);
    try {
      const res = await api.post('/reminders/check');
      toast.success(`${res.data.reminders_sent} Erinnerung(en) gesendet von ${res.data.orders_found} Bestellung(en)`);
      // Refresh status
      api.get('/reminders/status').then(r => setReminderStatus(r.data)).catch(() => {});
    } catch (e) { 
      toast.error(`Fehler: ${e.response?.data?.detail || e.message}`); 
    } finally { 
      setReminderLoading(false); 
    }
  };

  const createBackup = async (includeMedia = true) => {
    try {
      const res = await api.post(`/backup?include_media=${includeMedia}`);
      toast.success(res.data.message);
      api.get('/backups').then(r => setBackups(r.data)).catch(() => {});
    } catch (e) { toast.error(`Backup fehlgeschlagen: ${e.response?.data?.detail || e.message}`); }
  };

  const handleUploadBackup = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.json')) {
      toast.error('Nur JSON-Dateien erlaubt');
      return;
    }
    if (!window.confirm('ACHTUNG: Backup hochladen und einspielen? Alle aktuellen Daten (außer Benutzerkonten) werden überschrieben. Fortfahren?')) {
      e.target.value = '';
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/backups/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(res.data.message);
      api.get('/backups').then(r => setBackups(r.data)).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload fehlgeschlagen');
    }
    e.target.value = '';
  };

  const handleRestore = async (backupId) => {
    if (!window.confirm('ACHTUNG: Backup einspielen? Alle aktuellen Daten (außer Benutzerkonten) werden überschrieben. Fortfahren?')) return;
    setRestoring(backupId);
    try {
      const res = await api.post(`/backups/${backupId}/restore`);
      toast.success(res.data.message);
      api.get('/backups').then(r => setBackups(r.data)).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || 'Restore fehlgeschlagen'); }
    finally { setRestoring(null); }
  };

  const handleDownload = async (backupId) => {
    try {
      const response = await api.get(`/backups/${backupId}/download`, {
        responseType: 'blob'
      });
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `backup_${backupId}.json`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }
      // Create download link
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Backup heruntergeladen');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Download fehlgeschlagen');
    }
  };

  return (
    <div data-testid="admin-settings">
      <h1 className="text-2xl font-bold text-[#0E518D] mb-6">Einstellungen</h1>

      <div className="space-y-6">
        {/* Shop Settings - Mindestbestellwert + Erinnerung */}
        <Card className="bg-white rounded-sm shadow-sm border border-slate-100">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-[#0E518D] flex items-center gap-2"><ShoppingCart className="h-5 w-5" /> Shop-Einstellungen</CardTitle>
              <Button size="sm" onClick={saveShopSettings} className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm text-xs" data-testid="save-shop-settings">
                <Save className="h-3.5 w-3.5 mr-1" /> Speichern
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-sm font-medium">Mindestbestellwert (€)</Label>
                <Input 
                  type="number" 
                  step="0.01"
                  min="0"
                  value={shopSettings.min_order_value || ''} 
                  onChange={e => setShopSettings(s => ({ ...s, min_order_value: parseFloat(e.target.value) || 0 }))} 
                  className="mt-1 rounded-sm w-40" 
                  data-testid="shop-min-order-value" 
                />
                <p className="text-xs text-slate-400 mt-2">Bestellungen unter diesem Wert werden blockiert (bezieht sich auf Produktsumme).</p>
              </div>
              
              <div>
                <Label className="text-sm font-medium">Liefer- und Servicepauschale (€)</Label>
                <Input 
                  type="number" 
                  step="0.01"
                  min="0"
                  value={shopSettings.service_fee ?? 5} 
                  onChange={e => setShopSettings(s => ({ ...s, service_fee: parseFloat(e.target.value) || 0 }))} 
                  className="mt-1 rounded-sm w-40" 
                  data-testid="shop-service-fee" 
                />
                <p className="text-xs text-slate-400 mt-2">Fester Betrag, der einmal pro Bestellung hinzugefügt wird.</p>
              </div>
            </div>
            
            <Separator />
            
            {/* Standardkategorie für Shop */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <FolderOpen className="h-4 w-4 text-[#0E518D]" />
                <Label className="text-sm font-medium">Standardkategorie beim Shop-Start</Label>
              </div>
              <Select 
                value={shopSettings.default_category_id || 'none'} 
                onValueChange={v => setShopSettings(s => ({ ...s, default_category_id: v === 'none' ? '' : v }))}
              >
                <SelectTrigger className="w-full sm:w-80 rounded-sm" data-testid="shop-default-category">
                  <SelectValue placeholder="Keine Vorauswahl" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Keine Vorauswahl</SelectItem>
                  {categories.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400 mt-2">
                Diese Kategorie wird beim Öffnen der Shopseite automatisch als Filter vorausgewählt.
                <br />Der Nutzer kann die Auswahl jederzeit ändern.
              </p>
            </div>
            
            <Separator />
            
            {/* Erinnerungs-E-Mail Einstellungen */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Bell className="h-4 w-4 text-[#0E518D]" />
                <Label className="text-sm font-medium">Erinnerungs-E-Mail</Label>
              </div>
              <div className="flex items-center gap-3">
                <Input 
                  type="number" 
                  min="0"
                  max="14"
                  value={shopSettings.reminder_days ?? 2} 
                  onChange={e => setShopSettings(s => ({ ...s, reminder_days: parseInt(e.target.value) || 0 }))} 
                  className="rounded-sm w-20" 
                  data-testid="shop-reminder-days" 
                />
                <span className="text-sm text-slate-600">Tage vor Anreise</span>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                Automatische interne Erinnerung an info@gb-sylt.de. 
                <br />0 = am Anreisetag, 2 = zwei Tage vorher.
              </p>
              
              {/* Reminder Status */}
              {reminderStatus && (
                <div className="mt-4 p-3 bg-slate-50 rounded-sm border border-slate-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-slate-600">Erinnerungs-Status</span>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={triggerReminderCheck} 
                      disabled={reminderLoading}
                      className="h-7 text-xs rounded-sm"
                      data-testid="trigger-reminder-btn"
                    >
                      <Play className="h-3 w-3 mr-1" />
                      {reminderLoading ? 'Prüfe...' : 'Jetzt prüfen'}
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-slate-400">Zieldatum heute:</span>
                      <span className="ml-2 font-medium">{reminderStatus.target_date_for_today}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">Heute ausstehend:</span>
                      <span className="ml-2 font-medium">{reminderStatus.pending_today}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">Gesamt gesendet:</span>
                      <span className="ml-2 font-medium">{reminderStatus.total_sent}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">Letzte 7 Tage:</span>
                      <span className="ml-2 font-medium">{reminderStatus.sent_last_7_days}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Mail Settings */}
        <Card className="bg-white rounded-sm shadow-sm border border-slate-100">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-[#0E518D]">Microsoft Graph E-Mail</CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={testMail} disabled={testLoading} className="rounded-sm text-xs" data-testid="test-mail-btn">
                  <Send className="h-3.5 w-3.5 mr-1" /> {testLoading ? 'Sende...' : 'Test-Mail'}
                </Button>
                <Button size="sm" onClick={saveMailSettings} className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm text-xs" data-testid="save-mail-settings">
                  <Save className="h-3.5 w-3.5 mr-1" /> Speichern
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <Switch checked={mailSettings.enabled} onCheckedChange={v => setMailSettings(s => ({ ...s, enabled: v }))} data-testid="mail-enabled" />
              <Label className="text-sm">E-Mail-Versand aktiviert</Label>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Tenant ID</Label>
                <Input value={mailSettings.tenant_id || ''} onChange={e => setMailSettings(s => ({ ...s, tenant_id: e.target.value }))} className="mt-1 rounded-sm" data-testid="mail-tenant-id" />
              </div>
              <div>
                <Label className="text-sm">Client ID</Label>
                <Input value={mailSettings.client_id || ''} onChange={e => setMailSettings(s => ({ ...s, client_id: e.target.value }))} className="mt-1 rounded-sm" data-testid="mail-client-id" />
              </div>
              <div>
                <Label className="text-sm">Client Secret</Label>
                <Input type="password" value={mailSettings.client_secret || ''} onChange={e => setMailSettings(s => ({ ...s, client_secret: e.target.value }))} className="mt-1 rounded-sm" data-testid="mail-client-secret" />
              </div>
              <div>
                <Label className="text-sm">Absender E-Mail</Label>
                <Input value={mailSettings.sender_email || ''} onChange={e => setMailSettings(s => ({ ...s, sender_email: e.target.value }))} className="mt-1 rounded-sm" data-testid="mail-sender-email" />
              </div>
            </div>
            <p className="text-xs text-slate-400 italic">Wenn deaktiviert, werden E-Mails nur als Mock geloggt.</p>
          </CardContent>
        </Card>

        {/* Bankverbindung */}
        <Card className="bg-white rounded-sm shadow-sm border border-slate-100">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-[#0E518D] flex items-center gap-2"><Landmark className="h-5 w-5" /> Bankverbindung / Zahlungsinformationen</CardTitle>
              <Button size="sm" onClick={saveBankSettings} className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm text-xs" data-testid="save-bank-settings">
                <Save className="h-3.5 w-3.5 mr-1" /> Speichern
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Kontoinhaber</Label>
                <Input value={bankSettings.kontoinhaber || ''} onChange={e => setBankSettings(s => ({ ...s, kontoinhaber: e.target.value }))} className="mt-1 rounded-sm" data-testid="bank-kontoinhaber" />
              </div>
              <div>
                <Label className="text-sm">Bankname</Label>
                <Input value={bankSettings.bankname || ''} onChange={e => setBankSettings(s => ({ ...s, bankname: e.target.value }))} className="mt-1 rounded-sm" data-testid="bank-bankname" />
              </div>
              <div>
                <Label className="text-sm">IBAN</Label>
                <Input value={bankSettings.iban || ''} onChange={e => setBankSettings(s => ({ ...s, iban: e.target.value }))} className="mt-1 rounded-sm" data-testid="bank-iban" />
              </div>
              <div>
                <Label className="text-sm">BIC</Label>
                <Input value={bankSettings.bic || ''} onChange={e => setBankSettings(s => ({ ...s, bic: e.target.value }))} className="mt-1 rounded-sm" data-testid="bank-bic" />
              </div>
            </div>
            <div>
              <Label className="text-sm">Hinweis (optional)</Label>
              <Textarea value={bankSettings.hinweis || ''} onChange={e => setBankSettings(s => ({ ...s, hinweis: e.target.value }))} rows={2} className="mt-1 rounded-sm" placeholder="z.B. Zahlungsfrist, Hinweis..." data-testid="bank-hinweis" />
            </div>
            <Separator />
            <div className="bg-slate-50 p-4 rounded-sm">
              <p className="text-sm font-medium text-[#1E293B] mb-2">Verwendungszweck (automatisch)</p>
              <p className="text-xs text-[#475569] mb-1">Der Verwendungszweck wird bei jeder Bestellung automatisch generiert:</p>
              <p className="text-sm font-mono bg-white px-3 py-2 rounded-sm border border-slate-200 text-[#0E518D]">GS-2026-XXXXX | Buchung [Buchungsnummer]</p>
              <p className="text-xs text-slate-400 mt-2 italic">Die Vorgangsnummer (GS-...) und die Buchungsnummer des Gastes werden automatisch eingesetzt.</p>
            </div>
          </CardContent>
        </Card>

        {/* SEO */}
        <Card className="bg-white rounded-sm shadow-sm border border-slate-100">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-[#0E518D] flex items-center gap-2"><Search className="h-5 w-5" /> SEO</CardTitle>
              <Button size="sm" onClick={saveSeo} className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm text-xs" data-testid="save-seo">
                <Save className="h-3.5 w-3.5 mr-1" /> Speichern
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Meta Titel</Label>
                <Input value={seo.meta_title || ''} onChange={e => setSeo(s => ({ ...s, meta_title: e.target.value }))} className="mt-1 rounded-sm" data-testid="seo-title" />
              </div>
              <div>
                <Label className="text-sm">Slug</Label>
                <Input value={seo.slug || ''} onChange={e => setSeo(s => ({ ...s, slug: e.target.value }))} className="mt-1 rounded-sm" data-testid="seo-slug" />
              </div>
            </div>
            <div>
              <Label className="text-sm">Meta Beschreibung</Label>
              <Textarea value={seo.meta_description || ''} onChange={e => setSeo(s => ({ ...s, meta_description: e.target.value }))} rows={2} className="mt-1 rounded-sm" data-testid="seo-description" />
            </div>
            <div>
              <Label className="text-sm">Keywords</Label>
              <Input value={seo.keywords || ''} onChange={e => setSeo(s => ({ ...s, keywords: e.target.value }))} className="mt-1 rounded-sm" data-testid="seo-keywords" />
            </div>
            <Separator />
            <p className="text-sm font-medium text-[#1E293B]">Open Graph</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">OG Titel</Label>
                <Input value={seo.og_title || ''} onChange={e => setSeo(s => ({ ...s, og_title: e.target.value }))} className="mt-1 rounded-sm" data-testid="seo-og-title" />
              </div>
              <div>
                <Label className="text-sm">OG Bild URL</Label>
                <Input value={seo.og_image || ''} onChange={e => setSeo(s => ({ ...s, og_image: e.target.value }))} className="mt-1 rounded-sm" data-testid="seo-og-image" />
              </div>
            </div>
            <div>
              <Label className="text-sm">OG Beschreibung</Label>
              <Textarea value={seo.og_description || ''} onChange={e => setSeo(s => ({ ...s, og_description: e.target.value }))} rows={2} className="mt-1 rounded-sm" data-testid="seo-og-description" />
            </div>
          </CardContent>
        </Card>

        {/* Backup & Restore */}
        <Card className="bg-white rounded-sm shadow-sm border border-slate-100">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-lg text-[#0E518D] flex items-center gap-2"><Database className="h-5 w-5" /> Backup & Restore</CardTitle>
              <div className="flex gap-2 flex-wrap">
                <Button onClick={() => createBackup(true)} className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm text-xs" data-testid="create-backup-full-btn">
                  <Image className="h-3.5 w-3.5 mr-1" /> Vollständiges Backup
                </Button>
                <Button variant="outline" onClick={() => createBackup(false)} className="rounded-sm text-xs" data-testid="create-backup-data-btn">
                  <Database className="h-3.5 w-3.5 mr-1" /> Nur Daten
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-[#475569]">Erstellen und verwalten Sie Backups der Datenbank, Einstellungen und Medien.</p>
            
            {/* Upload Section */}
            <div className="bg-slate-50 p-4 rounded-sm border border-slate-200">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="text-sm font-medium text-slate-700">Backup hochladen & einspielen</p>
                  <p className="text-xs text-slate-500">JSON-Datei mit Daten und optionalen Medien</p>
                </div>
                <label className="cursor-pointer">
                  <input type="file" accept=".json" className="hidden" onChange={handleUploadBackup} data-testid="upload-backup-input" />
                  <Button variant="outline" asChild className="rounded-sm text-xs">
                    <span><Upload className="h-3.5 w-3.5 mr-1" /> Backup hochladen</span>
                  </Button>
                </label>
              </div>
            </div>
            {backups.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="backup-table">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-slate-600">Datum</th>
                      <th className="text-left px-3 py-2 font-medium text-slate-600">Typ</th>
                      <th className="text-left px-3 py-2 font-medium text-slate-600">Status</th>
                      <th className="text-right px-3 py-2 font-medium text-slate-600">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {backups.map(b => (
                      <tr key={b.id} className="hover:bg-slate-50/50" data-testid={`backup-row-${b.id}`}>
                        <td className="px-3 py-2 text-slate-600">{formatDateTime(b.created_at)}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            <Badge variant="outline" className="text-xs">
                              {b.type === 'manual' ? 'Manuell' : b.type === 'automatic' ? 'Automatisch' : b.type === 'uploaded_restore' ? 'Upload' : 'Restore'}
                            </Badge>
                            {b.include_media && <Badge className="text-xs bg-blue-100 text-blue-700">+Medien</Badge>}
                            {b.media_restored > 0 && <Badge className="text-xs bg-green-100 text-green-700">{b.media_restored} Medien</Badge>}
                          </div>
                        </td>
                        <td className="px-3 py-2"><Badge className="text-xs bg-green-100 text-green-700">{b.status}</Badge></td>
                        <td className="px-3 py-2 text-right space-x-1">
                          {b.type !== 'restore' && (
                            <>
                              <Button variant="outline" size="sm" onClick={() => handleDownload(b.id)}
                                className="text-xs h-7 px-2 rounded-sm" data-testid={`download-btn-${b.id}`}>
                                <Download className="h-3 w-3 mr-1" />
                                Download
                              </Button>
                              {currentAdmin?.role === 'admin' && (
                                <Button variant="outline" size="sm" disabled={restoring === b.id} onClick={() => handleRestore(b.id)}
                                  className="text-xs h-7 px-2 rounded-sm" data-testid={`restore-btn-${b.id}`}>
                                  <RotateCcw className={`h-3 w-3 mr-1 ${restoring === b.id ? 'animate-spin' : ''}`} />
                                  {restoring === b.id ? 'Wird eingespielt...' : 'Einspielen'}
                                </Button>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {backups.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Keine Backups vorhanden</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
